import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle } from 'react-leaflet';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { MapPin, Navigation, Clock } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import api from '../utils/api';

// Fix leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Custom marker icon
const staffIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const LiveMap = () => {
  const [staffLocations, setStaffLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [center, setCenter] = useState([28.6139, 77.2090]); // Default: Delhi

  useEffect(() => {
    fetchLocations();
    const interval = setInterval(fetchLocations, 10000); // Refresh every 10 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await api.get('/api/field-staff/admin/staff-locations');
      if (response.data.success) {
        const locations = response.data.locations;
        setStaffLocations(locations);
        
        // Set map center to first location
        if (locations.length > 0) {
          setCenter([locations[0].latitude, locations[0].longitude]);
        }
      }
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch staff locations');
      setLoading(false);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const getTimeDiff = (timestamp) => {
    const now = new Date();
    const then = new Date(timestamp);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${Math.floor(diffHours / 24)}d ago`;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <p>Loading map...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MapPin className="h-5 w-5" />
          Live Staff Locations
          <Badge variant="outline" className="ml-auto">
            {staffLocations.length} Active
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {error && (
          <div className="bg-red-50 text-red-600 p-3 rounded mb-4">
            {error}
          </div>
        )}
        
        {staffLocations.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <MapPin className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>No staff locations available</p>
          </div>
        ) : (
          <div className="space-y-4">
            <MapContainer 
              center={center} 
              zoom={12} 
              style={{ height: '500px', width: '100%', borderRadius: '8px' }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              />
              
              {staffLocations.map((location, index) => (
                <React.Fragment key={index}>
                  <Marker 
                    position={[location.latitude, location.longitude]}
                    icon={staffIcon}
                  >
                    <Popup>
                      <div className="p-2">
                        <h3 className="font-bold">{location.staff_name}</h3>
                        <p className="text-sm text-gray-600">{location.position}</p>
                        <div className="flex items-center gap-1 text-xs text-gray-500 mt-2">
                          <Clock className="h-3 w-3" />
                          {getTimeDiff(location.timestamp)}
                        </div>
                        {location.accuracy && (
                          <p className="text-xs text-gray-500 mt-1">
                            Accuracy: ±{Math.round(location.accuracy)}m
                          </p>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                  
                  {location.accuracy && (
                    <Circle
                      center={[location.latitude, location.longitude]}
                      radius={location.accuracy}
                      pathOptions={{ color: 'blue', fillColor: 'blue', fillOpacity: 0.1 }}
                    />
                  )}
                </React.Fragment>
              ))}
            </MapContainer>
            
            {/* Staff list */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {staffLocations.map((location, index) => (
                <div key={index} className="border rounded-lg p-3 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium">{location.staff_name}</p>
                      <p className="text-sm text-gray-600">{location.position}</p>
                    </div>
                    <Navigation className="h-4 w-4 text-blue-600" />
                  </div>
                  <div className="mt-2 text-xs text-gray-500 flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {getTimeDiff(location.timestamp)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default LiveMap;
